import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Sidebar } from './Sidebar';
import { TopNav } from './TopNav';

/**
 * Top-level layout shell. Sidebar (left) + TopNav (top) + main scroll area
 * with `<Outlet />` for route content. Matches
 * docs/specs/08-pa-jp-design-reference.md §5.
 *
 * Mobile (`<md`): sidebar collapses into a Sheet drawer triggered by a
 * hamburger button rendered before the TopNav controls.
 */
export const AppShell = () => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen w-full bg-efs-bg-app text-efs-text-primary">
      <div className="hidden md:block">
        <Sidebar />
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-60 p-0 md:hidden">
          <Sidebar onNavigate={() => setMobileOpen(false)} className="h-full w-full border-r-0" />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center md:block">
          <Button
            variant="ghost"
            size="icon"
            className="ml-2 md:hidden"
            aria-label={t('efs.common.nav.dashboard')}
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <TopNav />
          </div>
        </div>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-screen-2xl px-3 py-4 sm:px-6 sm:py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppShell;
